import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { MapPin, Zap, Euro, Plug, Filter, List, Map as MapIcon, Clock, MessageSquare, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";

const modeLabels: Record<string, { label: string; icon: typeof Zap }> = {
  instant: { label: "Sofort verfügbar", icon: Zap },
  request: { label: "Auf Anfrage", icon: MessageSquare },
  reservable: { label: "Reservierbar", icon: Clock },
};

const connectorTypes = ["Typ 1", "Typ 2", "CCS", "CHAdeMO", "Schuko"];

const Search = () => {
  const [view, setView] = useState<"list" | "map">("list");
  const [modeFilter, setModeFilter] = useState<string>("all");
  const [connectorFilter, setConnectorFilter] = useState<string>("all");
  const [maxPrice, setMaxPrice] = useState<number>(20);
  const [showFilters, setShowFilters] = useState(false);

  const { data: spots, isLoading } = useQuery({
    queryKey: ["search_spots"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("charging_spots")
        .select("*")
        .eq("active", true)
        .eq("approved", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filtered = useMemo(() => {
    if (!spots) return [];
    return spots.filter((s) => {
      if (modeFilter !== "all" && (s as any).availability_mode !== modeFilter) return false;
      if (connectorFilter !== "all" && s.connector_type !== connectorFilter) return false;
      if (Number(s.price_per_hour) > maxPrice) return false;
      return true;
    });
  }, [spots, modeFilter, connectorFilter, maxPrice]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h1 className="text-lg font-semibold text-foreground">Ladepunkte finden</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-3.5 w-3.5" />
              Filter
            </Button>
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setView("list")}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  view === "list" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setView("map")}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  view === "map" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                <MapIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      {showFilters && (
        <div className="border-b border-border bg-muted/30">
          <div className="container py-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {/* Mode filter */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Verfügbarkeit</label>
                <Select value={modeFilter} onValueChange={setModeFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Modi</SelectItem>
                    <SelectItem value="instant">Sofort verfügbar</SelectItem>
                    <SelectItem value="request">Auf Anfrage</SelectItem>
                    <SelectItem value="reservable">Reservierbar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Connector filter */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Steckertyp</label>
                <Select value={connectorFilter} onValueChange={setConnectorFilter}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Stecker</SelectItem>
                    {connectorTypes.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price filter */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Max. Preis: {maxPrice.toFixed(0)} €/h
                </label>
                <Slider
                  value={[maxPrice]}
                  onValueChange={([v]) => setMaxPrice(v)}
                  min={1}
                  max={50}
                  step={1}
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="container py-6">
        <p className="mb-4 text-sm text-muted-foreground">
          {isLoading ? "Suche…" : `${filtered.length} Ladepunkt${filtered.length !== 1 ? "e" : ""} gefunden`}
        </p>

        {view === "list" ? (
          <div className="space-y-3">
            {filtered.map((spot) => {
              const mode = (spot as any).availability_mode ?? "reservable";
              const ModeIcon = modeLabels[mode]?.icon ?? Clock;
              return (
                <Link
                  key={spot.id}
                  to={`/spot/${spot.id}`}
                  className="block rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground truncate">{spot.title}</h3>
                        <Badge variant="secondary" className="shrink-0 gap-1 text-xs">
                          <ModeIcon className="h-3 w-3" />
                          {modeLabels[mode]?.label}
                        </Badge>
                      </div>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0" /> {spot.address}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Plug className="h-3 w-3" /> {spot.connector_type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Zap className="h-3 w-3" /> {spot.power_kw} kW
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-foreground">
                        {Number(spot.price_per_hour).toFixed(2)} €
                      </p>
                      <p className="text-xs text-muted-foreground">pro Stunde</p>
                    </div>
                  </div>
                </Link>
              );
            })}

            {!isLoading && filtered.length === 0 && (
              <div className="rounded-lg border border-border bg-muted/30 p-8 text-center">
                <p className="text-muted-foreground">Keine Ladepunkte mit diesen Filtern gefunden.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-muted/30 p-12 text-center">
            <MapIcon className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">Kartenansicht wird bald verfügbar sein.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
